"use client";

import { useQuery } from "@tanstack/react-query";
import gql from "graphql-tag";
import type { GeneratedQuery } from "@aws-amplify/api-graphql";
import type { PatientJourneyFacts } from "@rmh/defs";
import { fetchGraphQLClient } from "../../../amplify/fetchGraphQLClient";
import { patientJourneyFactsQueryKey } from "./keys";

interface PatientJourneyFactsQueryVariables {
  input: string;
}

interface PatientJourneyFactsQueryData {
  patientJourneyFacts?: PatientJourneyFacts | null;
}

export const patientJourneyFactsQuery = gql`
  query patientJourneyFacts($input: String!) {
    patientJourneyFacts(input: $input) {
      input
      lookupRan
      note
      totalMs
      identity {
        name
        email
        dateOfBirth
        notes
      }
      lookupKey {
        email
        source
        note
        candidates {
          source
          email
          detail
        }
      }
      facts {
        key
        label
        value
        source
      }
      timeline {
        key
        at
        source
        title
        detail
        href
        zone
        summaryKind
        summaryRef
      }
      sources {
        source
        status
        ms
        note
        incomplete {
          unmappable
          truncated
          dropped
        }
      }
    }
  }
`.loc?.source.body as GeneratedQuery<
  PatientJourneyFactsQueryVariables,
  PatientJourneyFactsQueryData
>;

interface UsePatientJourneyFactsOptions {
  input: string;
  enabled?: boolean;
}

export const usePatientJourneyFacts = ({
  input,
  enabled = true,
}: UsePatientJourneyFactsOptions) => {
  return useQuery<PatientJourneyFacts>({
    queryKey: patientJourneyFactsQueryKey(input),
    queryFn: async () => {
      const response = await fetchGraphQLClient(patientJourneyFactsQuery, {
        input,
      });
      const facts = response.data?.patientJourneyFacts;

      if (!facts) {
        throw new Error("The Patient Journey lookup returned no report.");
      }

      return facts;
    },
    enabled: enabled && input.trim().length > 0,
    staleTime: Infinity,
    // Held for the whole browser session so an earlier patient restores
    // from the cache instead of re-running the sources.
    gcTime: Infinity,
    retry: false,
  });
};
